<div class="page-layout">
  <app-sidebar></app-sidebar>

  <div class="main-area">
    <app-navbar pageTitle="Version Review" pageSubtitle="Sentences in newly ingested files that change something already stored. Choose which stored sentence each one replaces, or keep them all."></app-navbar>

    <div class="content-wrap">

      <div class="error-banner" *ngIf="error()">{{ error() }}</div>

      <div class="mode-bar" *ngIf="settings() as st">
        <span class="mode-label" id="vr-mode-label">Approvals</span>
        <div class="mode-switch" role="radiogroup" aria-labelledby="vr-mode-label"
             [attr.title]="st.canChange ? null : 'Only a tenant admin can change this.'">
          <button type="button" role="radio" class="mode-option" [class.selected]="st.mode === 'manual'"
                  [attr.aria-checked]="st.mode === 'manual'" [disabled]="!st.canChange || savingMode()"
                  (click)="setMode('manual')">Manual</button>
          <button type="button" role="radio" class="mode-option" [class.selected]="st.mode === 'auto'"
                  [attr.aria-checked]="st.mode === 'auto'" [disabled]="!st.canChange || savingMode()"
                  (click)="setMode('auto')">Auto</button>
        </div>
        <p class="mode-hint">
          {{ modeHint() }}
          <span class="mode-changed" *ngIf="st.changedBy">Changed by {{ st.changedBy }}<ng-container *ngIf="st.changedAt"> · {{ st.changedAt | date: 'medium' }}</ng-container>.</span>
        </p>
      </div>

      <div class="stat-cards" *ngIf="stats() as s">
        <div class="stat-card">
          <span class="stat-value">{{ s.pending }}</span>
          <span class="stat-label">Waiting for review</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ s.approved }}</span>
          <span class="stat-label">Approved updates</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ s.rejected }}</span>
          <span class="stat-label">Rejected (kept both)</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ s.autoAccepted }}</span>
          <span class="stat-label">Decided automatically</span>
        </div>
      </div>

      <div class="toolbar">
        <label class="visually-hidden" for="vr-status">Show</label>
        <select id="vr-status" [(ngModel)]="statusFilter" (ngModelChange)="onFilterChange()">
          <option value="pending">Waiting for review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="auto">Decided automatically</option>
          <option value="">All</option>
        </select>
        <button class="refresh-btn" (click)="refresh()">Refresh</button>
        <span class="count" *ngIf="!loading()">{{ total() }} update{{ total() === 1 ? '' : 's' }}</span>
      </div>

      <div class="loading" *ngIf="loading()">Loading updates…</div>

      <ng-container *ngIf="!loading()">
        <!-- Waiting for review: one card per new sentence and old document; pick at most one. -->
        <div class="review-list" *ngIf="statusFilter() === 'pending'; else decidedList">
          <ng-container *ngIf="groups().length > 0; else emptyState">
            <article class="review-card" *ngFor="let g of groups()">
              <header class="review-head">
                <span class="change-chip" [class.removed]="isRemoval(g)">
                  {{ isRemoval(g) ? 'Removed in the new version'
                     : g.options.length > 1 ? g.options.length + ' possible matches' : changeTypeLabel(g.options[0].changeType) }}
                </span>
                <span class="newer-chip" *ngIf="newerSide(g.options[0]) as side">
                  {{ side === 'new' ? 'Incoming file is newer' : 'Incoming file is older' }}
                </span>
                <span class="same-batch" *ngIf="sameBatch(g) && !newerSide(g.options[0])" title="Both files arrived in the same batch, so 'incoming' only means processed later.">
                  Same batch: check which file is newer
                </span>
                <span class="status-pill pending">pending</span>
              </header>

              <!-- A sentence the new version no longer has: remove it from the older file, or keep it. -->
              <ng-container *ngIf="isRemoval(g); else replacement">
                <section class="version old">
                  <div class="version-label">In {{ g.oldDocumentName }} · missing from {{ g.newDocumentName }}</div>
                  <p class="version-text">{{ g.options[0].oldText }}</p>
                </section>
                <div class="choices" role="radiogroup" [attr.aria-label]="'Remove from ' + g.oldDocumentName + '?'">
                  <label class="choice" [class.selected]="chosen(g) === g.options[0].id">
                    <input type="radio" [name]="g.key" [checked]="chosen(g) === g.options[0].id" (change)="choose(g.key, g.options[0].id)" />
                    <span class="choice-body"><span class="version-text">Remove it from {{ g.oldDocumentName }}</span>
                      <span class="choice-meta">The new version dropped it; it leaves search and chat.</span></span>
                  </label>
                  <label class="choice none" [class.selected]="chosen(g) === none">
                    <input type="radio" [name]="g.key" [checked]="chosen(g) === none" (change)="choose(g.key, none)" />
                    <span class="choice-body"><span class="version-text">Keep it</span>
                      <span class="choice-meta">It still applies, or the new file isn't a full new version.</span></span>
                  </label>
                </div>
              </ng-container>

              <ng-template #replacement>
              <section class="version new">
                <div class="version-label">Incoming · {{ g.newDocumentName }}<span class="doc-date" *ngIf="g.options[0].newDocumentDate"
                     [title]="dateSourceHint(g.options[0].newDateSource)"> · dated {{ g.options[0].newDocumentDate | date: 'd MMM y' }}</span></div>
                <p class="version-text">{{ g.newText }}</p>
              </section>

              <div class="choice-title">Replaces which sentence in {{ g.oldDocumentName }}<span class="doc-date" *ngIf="g.options[0].oldDocumentDate"
                   [title]="dateSourceHint(g.options[0].oldDateSource)"> (dated {{ g.options[0].oldDocumentDate | date: 'd MMM y' }})</span>?</div>
              <div class="choices" role="radiogroup" [attr.aria-label]="'Replaces which sentence in ' + g.oldDocumentName">
                <label class="choice" *ngFor="let o of g.options" [class.selected]="chosen(g) === o.id">
                  <input type="radio" [name]="g.key" [checked]="chosen(g) === o.id" (change)="choose(g.key, o.id)" />
                  <span class="choice-body">
                    <span class="choice-meta">{{ changeTypeLabel(o.changeType) }} · {{ o.similarityPct }}% similar meaning</span>
                    <span class="version-text">{{ o.oldText }}</span>
                    <span class="diff" *ngIf="chosen(g) === o.id">
                      <span class="version-label">
                        {{ proposal(o) && !isWhole(g) ? 'Proposed update in ' + g.oldDocumentName : 'What changes' }}
                      </span>
                      <span class="diff-text">
                        <ng-container *ngFor="let d of diffFor(o.oldText, outcome(g, o))">
                          <del *ngIf="d.op === 'delete'">{{ d.text }}</del>
                          <ins *ngIf="d.op === 'insert'">{{ d.text }}</ins>
                          <span *ngIf="d.op === 'equal'">{{ d.text }}</span>
                          <span class="gap" *ngIf="d.op === 'gap'">…</span>
                          {{ ' ' }}
                        </ng-container>
                      </span>
                    </span>
                  </span>
                </label>
                <label class="choice none" [class.selected]="chosen(g) === none">
                  <input type="radio" [name]="g.key" [checked]="chosen(g) === none" (change)="choose(g.key, none)" />
                  <span class="choice-body"><span class="version-text">None of these, keep all</span></span>
                </label>
              </div>

              <!-- Edit replacement: keep the rest of the old sentence by editing it instead of swapping it out. -->
              <div class="edit-panel" *ngIf="chosenReview(g) as o">
                <div class="edit-links" *ngIf="!isEditing(g)">
                  <button type="button" class="link-btn" (click)="startEdit(g, o)">Edit</button>
                  <ng-container *ngIf="proposal(o)">
                    <button type="button" class="link-btn" *ngIf="!isWhole(g)" (click)="setWhole(g.key, true)">
                      Replace the whole sentence instead
                    </button>
                    <button type="button" class="link-btn" *ngIf="isWhole(g)" (click)="setWhole(g.key, false)">
                      Use the proposed update
                    </button>
                  </ng-container>
                </div>
                <ng-container *ngIf="isEditing(g)">
                  <label class="version-label" [for]="'edit-' + g.key">Replacement for the old sentence in {{ g.oldDocumentName }}</label>
                  <textarea class="edit-text" rows="4" [id]="'edit-' + g.key"
                            [ngModel]="editing()[g.key]" (ngModelChange)="setEdit(g.key, $event)"></textarea>
                  <p class="edit-hint">
                    This text takes the old sentence's place, so the rest of its context stays searchable.
                    "{{ g.newText }}" in {{ g.newDocumentName }} is kept as well.
                  </p>
                  <button type="button" class="link-btn" (click)="cancelEdit(g.key)">Use the new sentence as is</button>
                </ng-container>
              </div>

              </ng-template>

              <footer class="review-actions">
                <button class="action-btn history" *ngIf="!isRemoval(g)" [disabled]="!canKeepAsHistory(g)" (click)="keepAsHistory(g)"
                        title="Both were true at their time (e.g. an earlier role): the new sentence takes over, the old one is kept on the timeline.">
                  Both true, at different times
                </button>
                <button class="action-btn approve" [disabled]="!canConfirm(g)" (click)="confirm(g)">
                  {{ confirmLabel(g) }}
                </button>
              </footer>
            </article>
          </ng-container>
        </div>

        <!-- Approved / Rejected / All: one card per pair, with Undo. -->
        <ng-template #decidedList>
          <div class="review-list" *ngIf="reviews().length > 0; else emptyState">
            <article class="review-card" *ngFor="let r of reviews()">
              <header class="review-head">
                <span class="change-chip">{{ changeTypeLabel(r.changeType) }}</span>
                <span class="similarity">{{ r.similarityPct }}% similar meaning</span>
                <span class="status-pill" [class]="'status-pill ' + (isAutoAccepted(r) ? 'auto' : r.status)">{{ pillLabel(r) }}</span>
              </header>

              <p class="auto-reason" *ngIf="isAutoAccepted(r) && r.autoReason">{{ r.autoReason }}</p>

              <div class="versions">
                <section class="version old">
                  <div class="version-label">Current · {{ r.oldDocumentName || r.oldDocumentId }}<span class="doc-date" *ngIf="r.oldDocumentDate"
                       [title]="dateSourceHint(r.oldDateSource)"> · dated {{ r.oldDocumentDate | date: 'd MMM y' }}</span></div>
                  <p class="version-text">{{ r.oldText }}</p>
                </section>
                <section class="version new">
                  <div class="version-label">Incoming · {{ r.newDocumentName || r.documentId }}<span class="doc-date" *ngIf="r.newDocumentDate"
                       [title]="dateSourceHint(r.newDateSource)"> · dated {{ r.newDocumentDate | date: 'd MMM y' }}</span></div>
                  <p class="version-text">{{ r.changeType === 'removed' ? '(not in this version)' : r.newText }}</p>
                </section>
              </div>

              <section class="version edited" *ngIf="r.editedText">
                <div class="version-label">Replaced with your edit · {{ r.oldDocumentName || r.oldDocumentId }}</div>
                <p class="version-text">{{ r.editedText }}</p>
              </section>

              <footer class="review-decided">
                <span *ngIf="r.status === 'pending'">Waiting for review</span>
                <ng-container *ngIf="r.status !== 'pending'">
                  <span *ngIf="r.autoRejectedByReviewId; else manual">{{ closedLabel(r) }}</span>
                  <ng-template #manual>{{ decidedLabel(r) }}</ng-template>
                  <span *ngIf="r.reviewedBy && r.decidedBy !== 'auto'"> by {{ r.reviewedBy }}</span>
                  <span *ngIf="r.reviewedAt"> · {{ r.reviewedAt | date: 'medium' }}</span>
                  <button class="action-btn undo" *ngIf="!r.autoRejectedByReviewId" [disabled]="isBusy(r.id)" (click)="undo(r)">
                    {{ isBusy(r.id) ? 'Undoing…' : 'Undo' }}
                  </button>
                </ng-container>
              </footer>
            </article>
          </div>
        </ng-template>
      </ng-container>

      <ng-template #emptyState>
        <div class="empty-state">
          <p class="empty-title">Nothing to review</p>
          <p class="empty-text">When a newly ingested file changes a line that's already stored, it shows up here for approval.</p>
        </div>
      </ng-template>

      <div class="pagination" *ngIf="totalPages > 1">
        <button class="refresh-btn" [disabled]="page() === 1" (click)="goToPage(page() - 1)">Previous</button>
        <span>Page {{ page() }} of {{ totalPages }}</span>
        <button class="refresh-btn" [disabled]="page() === totalPages" (click)="goToPage(page() + 1)">Next</button>
      </div>
    </div>
  </div>
</div>
